import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FieldArray, useFormikContext } from 'formik'
import { uniq } from 'lodash'
import { fetchUserGroupsPrivileges } from 'services'
import { useErrorHandling } from 'context/errorHandling'
import { LinearLoader } from 'components/Shared/LinearLoader'
import { Accordion, AccordionDetails, AccordionSummary } from 'components/Shared/Accordion'
import PrivilegeCheckbox from './PrivilegeCheckbox/PrivilegeCheckbox'
import styles from './SelectPrivilegesFormField.module.css'
import { containsAny } from 'utils/utils'
import SelectAllInModuleCheckbox from './SelectAllInModuleCheckbox/SelectAllInModuleCheckbox'
import { ORDER_APPROVE_FOR_FULFILLMENT } from 'context/auth'
import { NumberField } from 'components/Shared/NumberField'
import { Typography } from '@material-ui/core'
import { CurrencySelectField } from 'components/Shared/CurrencySelectField'

export default function SelectPrivilegesFormField ({ disabled }) {
  const [allPrivileges, setAllPrivileges] = useState({})
  const { values, setFieldValue } = useFormikContext()
  const { t } = useTranslation()
  const { handleError } = useErrorHandling()

  useEffect(() => {
      fetchUserGroupsPrivileges()
        .then(setAllPrivileges)
        .catch(handleError)
    }, [handleError]
  )

  const { moduleToPrivileges, privilegeToDependentPrivileges } = allPrivileges

  function removeFromValues (valuesToRemove) {
    setFieldValue('privileges', values.privileges.filter(privilege => !valuesToRemove.includes(privilege)))
  }

  function getDependentPrivileges (privilege) {
    return privilegeToDependentPrivileges[privilege]
      .flatMap(p => [p, ...getDependentPrivileges(p)])
      .flat()
  }

  function addToValues (valuesToAdd) {
    const dependentPrivileges = valuesToAdd.flatMap(value => getDependentPrivileges(value))
    setFieldValue('privileges', uniq([...values.privileges, ...valuesToAdd, ...dependentPrivileges]))
  }

  function onCheckboxClick (event, privileges) {
    event.stopPropagation()
    if (event.target.checked) addToValues(privileges)
    else removeFromValues(privileges)
  }

  function getPrivilegesBlockingUnchecking (privileges) {
    return Object.keys(privilegeToDependentPrivileges)
      .filter(p => values.privileges.includes(p) && containsAny(privilegeToDependentPrivileges[p], privileges))
  }

  if (!moduleToPrivileges) {
    return <LinearLoader className={styles.loader}/>
  }

  const renderContractorLimitFormIfRequired = privilege => {
    if (privilege !== ORDER_APPROVE_FOR_FULFILLMENT) return null
    if (!values.privileges.includes(privilege)) return null

    return <div className={styles.orderApproveContainer}>
      <Typography className={styles.orderApproveHeader}>
        {t('userGroups.form.orderApproveProperties.title')}
      </Typography>
      <div className={styles.orderApproveFormContainer}>
        <NumberField
          className={styles.orderApprovePercent}
          fieldName="orderApproveProperties.percent"
          label="userGroups.form.orderApproveProperties.percent"
        />
        <NumberField
          className={styles.orderApproveMaxValue}
          fieldName="orderApproveProperties.maxValue.amount"
          label="userGroups.form.orderApproveProperties.maxValue"
        />
        <CurrencySelectField
          className={styles.orderApproveCurrency}
          fieldName="orderApproveProperties.maxValue.currencyCode"
          label="userGroups.form.orderApproveProperties.currency"
        />
      </div>
    </div>
  }

  return <FieldArray
    name={'privileges'}
    render={() => (
      <>
        {Object.keys(moduleToPrivileges).sort()
          .map(module => {
              const modulePrivileges = moduleToPrivileges[module]
              return <Accordion key={module} className={styles.root}>
                <AccordionSummary classes={{ root: styles.summaryRoot }}>
                  <SelectAllInModuleCheckbox
                    modulePrivileges={modulePrivileges}
                    onModuleCheckboxClick={onCheckboxClick}
                    getPrivilegesBlockingUnchecking={getPrivilegesBlockingUnchecking}/>
                  <div className={styles.title}>
                    {t('privileges.modules.' + module)}
                  </div>
                </AccordionSummary>
                <AccordionDetails className={styles.details}>
                  <div className={styles.moduleContainer}>
                    {modulePrivileges
                      .sort(byCustomOrder)
                      .map(privilege => <PrivilegeCheckbox key={privilege}
                                                           privilege={privilege}
                                                           checked={values.privileges.includes(privilege)}
                                                           onChange={event => onCheckboxClick(event, [privilege])}
                                                           privilegesBlockingUnchecking={getPrivilegesBlockingUnchecking([privilege])}
                                                           disabled={disabled}
                                                           bottomContent={renderContractorLimitFormIfRequired(privilege)}/>
                      )
                    }
                  </div>
                </AccordionDetails>
              </Accordion>
            }
          )}
      </>
    )}
  />
}

function byCustomOrder (a, b) {
  if (b.endsWith('READ') && !a.endsWith('READ')) {
    return 1
  }
  if (b.endsWith('CREATE') && !endsWithAny(a, ['CREATE', 'READ'])) {
    return 1
  }
  if (b.endsWith('EDIT') && !endsWithAny(a, ['CREATE', 'EDIT', 'READ'])) {
    return 1
  }
  if (b.endsWith('WRITE') && !endsWithAny(a, ['CREATE', 'EDIT', 'READ', 'WRITE'])) {
    return 1
  }
  if (b.endsWith('DELETE') && !endsWithAny(a, ['CREATE', 'EDIT', 'READ', 'WRITE', 'DELETE'])) {
    return 1
  }
  return 0
}

function endsWithAny (value, suffixes) {
  return suffixes.some(suffix => value.endsWith(suffix))
}